// src/components/UserGallery/AdminUserEditor.js
import React, { useState, useEffect } from 'react';
import LoadingSpinner from '../LoadingSpinner'; // Adjust path based on your project structure
import EditableUserRow from './EditableUserRow'; // Ensure this path is correct

const API_DOMAIN = "https://test-api.stainedglassusa.com"; // Your API domain

function AdminUserEditor() {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [fetchError, setFetchError] = useState(null); // Dedicated state for initial fetch errors

  // Function to update a single user in the state array after a successful edit
  const handleUserUpdateSuccess = (updatedUser) => {
    setUsers(currentUsers =>
      currentUsers.map(u => (u.id === updatedUser.id ? updatedUser : u))
    );
  };

  // Fetch users when the component mounts
  useEffect(() => {
    const token = localStorage.getItem('token');
    setLoading(true);
    setFetchError(null); // Clear any previous fetch errors

    if (!token) {
      setFetchError("Authentication token not found. Please log in as an administrator.");
      setLoading(false);
      return;
    }

    fetch(`${API_DOMAIN}/users`, { // This assumes your /users endpoint is configured to return all users for an admin
      headers: { 'Authorization': `Bearer ${token}` }
    })
      .then(res => {
        if (!res.ok) {
          // Handle specific HTTP error statuses
          if (res.status === 401 || res.status === 403) {
            throw new Error(`Authorization Error (${res.status}): You do not have permission to view this page. Ensure you are logged in as an administrator.`);
          }
          // Attempt to get more detail from response body
          return res.text().then(text => {
            try {
              const errorBody = JSON.parse(text);
              throw new Error(`HTTP Error ${res.status}: ${errorBody.error || errorBody.message || res.statusText}`);
            } catch {
              throw new Error(`HTTP Error ${res.status}: ${res.statusText}. Response: ${text.substring(0, 100)}...`);
            }
          });
        }
        return res.json();
      })
      .then(data => {
        if (Array.isArray(data)) {
           setUsers(data);
        } else {
           console.error("API response for /users is not an array:", data);
           throw new Error("Received invalid data format from server. Expected an array of users.");
        }
      })
      .catch(err => {
        console.error("Admin Users Fetch Error:", err);
        if (err instanceof SyntaxError) {
          setFetchError("Failed to parse server response. Expected JSON.");
        } else {
          setFetchError(err.message);
        }
      })
      .finally(() => {
        setLoading(false);
      });
  }, []); // Empty dependency array means this effect runs once on mount

  // Render Logic
  if (loading) {
    return <LoadingSpinner />;
  }

  if (fetchError) {
    return (
      <div style={{ color: 'red', padding: '20px', textAlign: 'center', backgroundColor: '#fff0f0', border: '1px solid #f5c6cb', borderRadius: '8px', margin: '20px' }}>
        <h2>Error Loading Users</h2>
        <p>{fetchError}</p>
        <p>Please ensure you are logged in as an administrator and your API server is running correctly.</p>
      </div>
    );
  }

  return (
    <div style={{ padding: '20px', maxWidth: '100%', overflowX: 'auto' }}>
      <h1>Bulk Edit Users (Admin Panel)</h1>
      <p>Found {users.length} registered users.</p>

      {users.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '20px', color: '#555' }}>
          No users found.
        </div>
      ) : (
        <table className="admin-user-table" style={{ width: '100%', borderCollapse: 'collapse', marginTop: '20px', minWidth: '1200px' }}>
          <thead>
            <tr style={{ borderBottom: '2px solid black', backgroundColor: '#f2f2f2' }}>
              <th style={{ textAlign: 'left', padding: '10px', whiteSpace: 'nowrap' }}>ID</th>
              <th style={{ textAlign: 'left', padding: '10px', whiteSpace: 'nowrap' }}>Username</th>
              <th style={{ textAlign: 'left', padding: '10px', whiteSpace: 'nowrap' }}>Email</th>
              <th style={{ textAlign: 'left', padding: '10px', whiteSpace: 'nowrap' }}>Phone</th>
              <th style={{ textAlign: 'left', padding: '10px', whiteSpace: 'nowrap' }}>Address</th>
              <th style={{ textAlign: 'left', padding: '10px', whiteSpace: 'nowrap' }}>Zip</th>
              <th style={{ textAlign: 'left', padding: '10px', whiteSpace: 'nowrap' }}>City</th>
              <th style={{ textAlign: 'left', padding: '10px', whiteSpace: 'nowrap' }}>State</th>
              <th style={{ textAlign: 'left', padding: '10px', whiteSpace: 'nowrap' }}>Street</th>
              <th style={{ textAlign: 'center', padding: '10px', whiteSpace: 'nowrap' }}>Email Validated</th>
              <th style={{ textAlign: 'center', padding: '10px', whiteSpace: 'nowrap' }}>Admin?</th>
              <th style={{ textAlign: 'center', padding: '10px', whiteSpace: 'nowrap' }}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {users.map(user => (
              <EditableUserRow
                key={user.id}
                user={user}
                token={localStorage.getItem('token')}
                onUpdateSuccess={handleUserUpdateSuccess}
              />
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}

export default AdminUserEditor;